<template>
    <b-form>
        <b-form-group
            :label-for="`start`"
            :label-cols="formColumn"
            label="Date:"
        >
            <vet-datetime
                id="start"
                v-model="data.start"
                v-validate="'required'"
                :container="data.modalWindow"
                :placeholder="{
                    date: 'Payment date (required)',
                    time: 'Payment time (required)'
                }"
                :class="{ 'border-danger': errors.has('start'), rounded: true }"
                rules="required"
                format="moment"
                name="start"
            ></vet-datetime>
        </b-form-group>
        <b-form-group
            :label-for="`client_id`"
            :label-cols="formColumn"
            label="Client:"
        >
            <vet-select
                id="client_id"
                v-model="data.client"
                v-validate="'required'"
                :class="{
                    'border-danger': errors.has('client_id'),
                    rounded: true
                }"
                :show-menu="false"
                module="client"
                placeholder="Client Name (required)"
                name="client_id"
                :clearOnSelect="false"
            />
        </b-form-group>
        <b-form-group
            :label-for="`apply_to`"
            :label-cols="formColumn"
            label="Apply To:"
        >
            <v-select
                id="apply_to"
                ref="vselect"
                v-model="data.invoices"
                v-validate="'required'"
                :class="{ 'border-danger': errors.has('apply_to') }"
                :options="applyToList"
                name="apply_to"
                placeholder="Apply to (required)"
                label="name"
                multiple
            >
                <template slot="no-options">
                    <span>No matches found</span>
                </template>
            </v-select>
        </b-form-group>
        <b-form-group
            :label-for="`credits`"
            :label-cols="formColumn"
            label="Credits:"
        >
            <v-select
                id="credits"
                ref="vselect"
                v-model="data.credits"
                :class="{ 'border-danger': errors.has('credits') }"
                :options="_.difference(data.creditsList, data.credits)"
                :disabled="hasDeposit || isApplyToFund"
                v-validate="data.payment_type === CREDIT_ONLY ? 'required' : ''"
                name="credits"
                :placeholder="
                    data.payment_type === CREDIT_ONLY
                        ? 'Credits (required)'
                        : 'Credits (optional)'
                "
                label="name"
                multiple
            >
                <template slot="no-options">
                    <span>No matches found</span>
                </template>
            </v-select>
        </b-form-group>
        <b-form-group
            :label-for="`collected`"
            :label-cols="formColumn"
            label="Collected:"
            v-if="data.payment_type !== CREDIT_ONLY"
        >
            <input
                id="collected"
                v-model="data.payment_amount"
                v-validate="{
                    required: true,
                    min_value: 0.01,
                    max_value: 999999
                }"
                :class="{ 'border-danger': errors.has('collected') }"
                :disabled="data.payment_amount < 0"
                placeholder="Amount collected (required)"
                name="collected"
                type="number"
                step="0.01"
                class="form-control"
            />
        </b-form-group>
        <b-form-group
            :label-for="`payment_type`"
            :label-cols="formColumn"
            label="Type:"
        >
            <select
                id="payment_type"
                ref="payment_type"
                v-model="data.payment_type"
                v-validate="'excluded:-1'"
                :disabled="
                    data.payment_amount <= 0 && data.credits.length === 0
                "
                :class="{ 'border-danger': errors.has('payment_type') }"
                name="payment_type"
                class="form-control"
                @change="_selectPaymentTypeObject"
            >
                <option value="-1"
                    >Please select a payment type (required)</option
                >
                <option v-if="data.credits.length > 0" :value="CREDIT_ONLY"
                    >Use Credits Only</option
                >
                <option
                    v-for="payment_type in paymentTypeList"
                    :key="payment_type.id"
                    :value="payment_type.id"
                >
                    <span
                        v-if="payment_type.category_id === 2 && (payment_type.balance && payment_type.balance > 0)"
                        >{{
                            getFundName(payment_type.name, payment_type.balance)
                        }}
                    </span>
                    <span v-else>{{ payment_type.name }}</span>
                </option>
            </select>
        </b-form-group>
        <b-form-group
            v-if="is_cc_card_in"
            :label-for="`payment_method_name`"
            :label-cols="formColumn"
            label="Payment Method:"
        >
            <vet-select
                id="payment_method_name"
                :key="data.payment_type_object.id"
                v-model="data.payment_method_name"
                v-validate="'required'"
                :disabled="data.payment_amount <= 0"
                :payer="data.client"
                :is-extension="'3'"
                :cc-processor="data.credit_card_processor"
                :class="{ 'border-danger': errors.has('payment_method_name') }"
                module="payment-method"
                placeholder="Payment Method (required)"
                name="payment_method_name"
                @options-loaded="_selectPrimaryMethod"
            />
        </b-form-group>
        <b-form-group
            v-if="is_cc_terminal"
            :label-for="`terminal`"
            :label-cols="formColumn"
            label="Terminal:"
        >
            <b-form-select
                id="terminal"
                v-model="data.terminal"
                v-validate="'excluded:0'"
                :class="{ 'border-danger': errors.has('terminal') }"
                :disabled="is_no_terminal"
                name="terminal"
            >
                <option
                    v-for="(value, key) in data.terminals"
                    :key="key"
                    :value="key"
                    >{{ value }}</option
                >
            </b-form-select>
        </b-form-group>
        <!-- stripe terminal simulator start -->
        <b-form-group
            v-if="is_stripe_terminal && env.VUE_APP_ENV !== 'PROD'"
            :label-for="`simulator`"
            :label-cols="formColumn"
            label="Simulator:"
        >
            <b-form-select
                id="simulator"
                v-model="data.isSimulator"
                :options="[
                    {
                        text: 'Real device',
                        value: 0
                    },
                    {
                        text: 'Simulator',
                        value: 1
                    }
                ]"
                name="simulator"
            >
            </b-form-select>
        </b-form-group>
        <b-form-group
            v-if="data.isSimulator"
            :label-for="`testCardNumber`"
            :label-cols="formColumn"
            label="Test Card:"
        >
            <input
                id="testCardNumber"
                v-model="data.testCardNumber"
                type="text"
                class="form-control"
                name="testCardNumber"
                placeholder="4242424242424242"
            />
        </b-form-group>
        <!-- stripe terminal simulator end -->
        <b-form-group
            :label-for="`change`"
            :label-cols="formColumn"
            label="Change:"
            v-if="data.payment_type !== CREDIT_ONLY"
        >
            <select
                v-model="data.change"
                :disabled="
                    payable ||
                    !has_invoice ||
                    change_amount <= 0 ||
                    isApplyToFund
                "
                class="form-control"
            >
                <option
                    v-if="data.change == '0' || isApplyToFund || isPaymentFund"
                    value="0"
                    >No change</option
                >
                <option
                    v-if="is_cash_selected && !payable && !hasDeposit"
                    value="1"
                    >{{ data.currency_symbol }}{{ change_amount }} (cash
                    back)</option
                >
                <option v-if="!payable" value="2"
                    >{{ data.currency_symbol
                    }}{{ change_amount }} (credit)</option
                >
                <template
                    v-if="!isApplyToFund && !isPaymentFund && !is_stripe_acss"
                >
                    <option
                        v-for="(fund, i) in fundList"
                        :value="fund"
                        :key="fund.name + i"
                    >
                        {{ getNameForChange(fund.name, change_amount) }}
                    </option>
                </template>
            </select>
        </b-form-group>
        <b-form-group
            :label-for="`documents`"
            :label-cols="formColumn"
            label="Documents:"
        >
            <v-select
                id="documents"
                ref="vselect"
                v-model="data.documents"
                :options="_.difference(docOptions, data.documents)"
                :disabled="data.export_type == 4"
                name="documents"
                placeholder="Documents (optional)"
                label="label"
                multiple
            >
                <template slot="no-options">
                    <span>No matches found</span>
                </template>
            </v-select>
        </b-form-group>
        <b-form-group
            :label-for="`export_type`"
            :label-cols="formColumn"
            label="Export:"
        >
            <b-form-select id="export_type" v-model="data.export_type">
                <option
                    v-for="value in exportOptions"
                    :key="value.id"
                    :value="value.id"
                    >{{ value.label }}</option
                >
            </b-form-select>
        </b-form-group>
        <b-form-group
            v-if="data.export_type == EXPORT_EMAIL"
            :label-for="`export_message`"
            :label-cols="formColumn"
            label="Message:"
        >
            <ContentEditable
                id="export_message"
                v-model="data.export_message"
                :no-h-t-m-l="false"
                tag="div"
                class="form-control"
            ></ContentEditable>
        </b-form-group>

        <b-form-group
            v-if="
                hasInvoices &&
                (data.export_type == EXPORT_EMAIL ||
                    data.export_type == EXPORT_PRINT)
            "
            label-for="include_declined"
            :label-cols="formColumn"
            label="Declined Items:"
        >
            <b-form-checkbox
                id="include_declined"
                v-model="data.includeDeclined"
                name="include_declined"
                plain
                class="col-form-label"
            >
                <span class="fw-normal">{{
                    TEXT_INCLUDE_DECLINED_ITEMS_INVOICE
                }}</span>
            </b-form-checkbox>
        </b-form-group>

        <b-form-group
            :label-for="`notes`"
            :label-cols="formColumn"
            label="Notes:"
            v-if="data.payment_type !== CREDIT_ONLY"
        >
            <input
                id="notes"
                v-model="data.notes"
                type="text"
                class="form-control"
                name="notes"
                placeholder="Notes (optional) "
            />
        </b-form-group>
    </b-form>
</template>
<style></style>
<script>
/**
 * @name PaymentCreate
 * @description New Payment Dialog
 * Rules :
 * - Cannot use credit to deposit to fund
 * - There is no balance for credit
 * - Allow user to pay using credit only :
 *     - will be considered as applying credit to invoice,
 *     - no payment will be recorded
 */
import { get, some, difference, isEmpty, concat, remove } from "lodash";
import vetSelect from "@/module/commons/vet-select.vue";
import vetDatetime from "@/module/commons/date-time-picker.vue";
import vSelect from "vue-select";
import PaymentDialogHelper from "@/module/payment/helper.js";
import ContentEditable from "@/module/commons/content-editable-template";
import Billings from "@/services/client-billings";
import FundMixin from "@/mixins/fund.js";
import Email from "@/services/email";
import { TEXT_INCLUDE_DECLINED_ITEMS_INVOICE } from "@/constants/billings.js";

const FUND = "FUND";
const CREDIT_ONLY = "CREDIT_ONLY";
const NO_EXPORT = 0;
const EXPORT_EMAIL = 1;
const EXPORT_PRINT = 3;

export default {
    name: "PaymentCreate",
    components: {
        vetSelect,
        vetDatetime,
        vSelect,
        ContentEditable
    },
    mixins: [PaymentDialogHelper, FundMixin],
    props: {
        data: {
            type: Object,
            default() {}
        },
        isPayNow: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            datePickerOptions: {
                format: "DD/MM/YYYY"
            },
            timePickerOptions: {
                format: "hh:mm A"
            },
            waitForPaymentMethod: false,
            firstLoad: true,
            invTotalMinusCredits: 0,
            CREDIT_ONLY,
            TEXT_INCLUDE_DECLINED_ITEMS_INVOICE,
            NO_EXPORT,
            EXPORT_EMAIL,
            EXPORT_PRINT
        };
    },
    computed: {
        formColumn() {
            return this.col ? this.col : 3;
        },
        col() {
            return this.is_cc_card_in ? 4 : 3;
        },
        hasDeposit() {
            return some(this.data.invoices, { id: 0 });
        },
        payable() {
            return (
                parseFloat(this.data.outstanding_amount) === 0 &&
                parseFloat(this.data.credit_amount) >=
                    parseFloat(this.data.invoice_amount)
            );
        },
        applyToList() {
            if (isEmpty(this.data.client)) {
                return [];
            }

            let invoices = difference(
                this.data.invoicesList,
                this.data.invoices
            );

            return concat(invoices, this.fundList);
        },
        isApplyToFund() {
            return some(this.data.invoices, ["type", FUND]);
        },
        isCollectingMore() {
            return (
                Number(this.data.payment_amount) > 0 &&
                Number(this.data.payment_amount) >
                    Number(this.invTotalMinusCredits)
            );
        },
        isAmountMoreFund() {
            if (this.isPaymentFund) {
                let fund = this.getFundValue(this.data.payment_type);

                return Number(this.data.payment_amount) > fund.balance;
            }

            return false;
        },
        paymentTypeList() {
            let self = this;
            if (
                !this.isApplyToFund &&
                !this.isChangeFund &&
                !this.isCollectingMore &&
                !this.isAmountMoreFund
            ) {
                return _.filter(this.data.paymentTypes, function (item) {
                    return (
                        item.category_id !== 2 ||
                        (item.category_id == 2 &&
                            item.balance &&
                            item.balance > 0)
                    );
                });
            }

            return _.filter(this.data.paymentTypes, function (item) {
                return item.category_id !== 2;
            });
        },
        validatePaymentType() {
            // can remove
            return isEmpty(this.data.credits);
        },
        docOptions() {
            if (this.data.payment_type === CREDIT_ONLY) {
                /**
                 * Action : Filter out receipt from the options
                 * Reason : Because applying credit only doesnt record any payment
                 */
                return _.filter(this.data.documentsList, function (doc) {
                    return doc.id !== 5;
                });
            }

            return this.data.documentsList;
        },
        exportOptions() {
            if (this.data.payment_type === CREDIT_ONLY) {
                /**
                 * Action : Filter out SMS (text) receipt from the options
                 * Reason : Because theres no document receipt option for CREDIT ONLY
                 */
                return _.filter(this.data.export_types, function (expType) {
                    return expType.id !== 4;
                });
            }

            return this.data.export_types;
        },
        hasInvoices() {
            return some(this.data.invoices, { is_invoice: true });
        }
    },
    watch: {
        "data.client": {
            handler(newData, oldData) {
                let self = this;
                self.data.client_id = newData ? newData.id : null;
                self.data.client_email = newData ? newData.email : null;

                // when data.client was not passed via props, set firstload to false on first change
                if (oldData == null) {
                    self.firstLoad = false;
                }

                self.data.invoicesList = [];
                if (newData) {
                    let appendFirstload =
                        oldData && oldData.id == newData.id ? false : true;

                    this.getInvoiceList(newData.id, appendFirstload).finally(
                        data => {
                            self.errors.remove("apply_to");
                        }
                    );
                } else {
                    self.data.invoices = [];
                    self.errors.remove("apply_to");
                }
            }
        },
        "data.invoices"(newValue, oldValue) {
            let self = this;

            if (this.isApplyToFund && newValue.length > 1) {
                // allow only 1 selection (new)
                // when there is Fund in the selections selected
                let newSelection = difference(newValue, oldValue);
                this.data.invoices = newSelection;
            }

            if (this.isApplyToFund) {
                // credits not allowed when there is fund in apply to
                this.data.credits = [];

                // this to remove acss debit payment type when fund is selected
                // current logic not support acss stripe to pay as charitable fund
                _.remove(this.paymentTypeList, function (n) {
                    return _.get(n, "name", "").indexOf("ACSS Debit") >= 0;
                });
            }

            if (this.hasDeposit || this.isApplyToFund) {
                self.data.credits = [];

                if (this.data.invoices.length === 1) {
                    let defaultDoc = _.filter(this.data.documentsList, doc =>
                        _.includes([5], doc.id)
                    );
                    this.data.documents = defaultDoc;
                }
            }
            this.updateCollected();
            this.$emit("invoice-changed", newValue);
        },
        "data.credits"(newValue, oldValue) {
            this.updateCollected();

            if (newValue !== oldValue) {
                this.data.payment_type = -1;
            }
        },
        "data.outstanding_amount"() {
            this._selectChange();
        },
        "data.payment_amount"(value) {
            this._selectChange();

            if (value <= 0) {
                this.data.payment_type = -1;
            }

            if (this.data.export_type === EXPORT_EMAIL) {
                this.getEmailMessages(this.data.client_id);
            }

            if (this.isCollectingMore || this.isAmountMoreFund) {
                this.data.payment_type = -1;
            }
        },
        "data.payment_type"(value) {
            this._selectPaymentTypeObject();
            this._selectChange();
            if (this.is_cc_card_in) {
                this.waitForPaymentMethod = true;
            }

            if (this.isPaymentFund) {
                let self = this;
                let selectedFund = _.find(self.fundList, function (fund) {
                    return fund.id == value;
                });
                if (
                    selectedFund &&
                    selectedFund.balance < self.data.payment_amount
                ) {
                    self.data.payment_amount = selectedFund.balance;
                }
            }

            if (value === CREDIT_ONLY) {
                /**
                 * Remove receipt from selection/param
                 * @param  data.documents
                 */
                this.data.documents = _.filter(this.data.documents, function (
                    doc
                ) {
                    return doc.id !== 5;
                });
                if (this.data.export_type == 4) {
                    /**
                     * Reset field/param
                     * @param data.export_type
                     */
                    this.data.export_type = 0;
                }
            }
        },
        "data.export_type"(value) {
            if (this.data.client_email && value === 1) {
                this.getEmailMessages(this.data.client_id);
            }

            if (value === 4) {
                this.data.documents = [];
            }
        },
        isApplyToFund(value) {
            if (value && this.data.invoices.length === 1) {
                this.$emit("update", {
                    fund_id: this.data.invoices[0].id,
                    deposit_to_fund: 1,
                    pay_from_fund: 0,
                    change_to_fund: 0
                });
            } else {
                this.$emit("update", { deposit_to_fund: 0 });
            }
        },
        isPaymentFund(value) {
            if (value) {
                this.$emit("update", {
                    fund_id: this.data.payment_type,
                    deposit_to_fund: 0,
                    pay_from_fund: 1,
                    change_to_fund: 0
                });
            } else {
                this.$emit("update", { pay_from_fund: 0 });
            }
        },
        isChangeFund(value) {
            if (value && this.data.change) {
                this.$emit("update", {
                    fund_id: this.data.change.id,
                    deposit_to_fund: 0,
                    pay_from_fund: 0,
                    change_to_fund: 1
                });
            } else {
                this.$emit("update", { change_to_fund: 0 });
            }
        }
    },

    mounted() {
        this.loading = true;
        this.datePickerOptions.format = this.getDateFormat();
        this.client_id = this.data.client_id;
        this.data.start = moment().tz(this._timeZone);

        if (!this.isPayNow) {
            this.getFundList();
        }

        if (!this.data.account_balance) {
            this.data.account_balance =
                this.$root.setting.currency_symbol + "0.00";
        }
    },
    methods: {
        shown() {
            this._updateCheckoutPaymentAmount();
        },
        async getInvoiceList(client_id, appendFirstload = true) {
            let self = this;

            let response = await Billings.getInvoicesWithBalance(client_id);
            if (response) {
                self.data.invoicesList =
                    _.get(response, "resource.invoices").map(inv =>
                        Object.assign(inv, { is_invoice: true })
                    ) || [];

                if (
                    self.firstLoad &&
                    self.data.invoices.length > 0 &&
                    appendFirstload
                ) {
                    self.firstLoad = false;
                    let match = null;

                    _.forEach(self.data.invoicesList, element => {
                        if (element.id == self.data.invoices[0].id) {
                            match = element;
                        }
                    });
                    if (match) {
                        self.data.invoices = [match];
                    }
                } else {
                    self.data.invoices = [...self.data.invoicesList] || [];
                }

                self.data.invoicesList.push({ name: "Deposit", id: 0 });

                self.data.credits = _.get(response, "resource.credits") || []; // -- default selected credits
                self.data.creditsList =
                    _.get(response, "resource.credits") || [];
                self.data.credits_balance =
                    _.get(response, "resource.credits_balance") || 0;
                self.data.documentsList =
                    _.get(response, "resource.documents") || [];

                let docs = [];
                docs = _.get(response, "resource.documents");
                docs.map(function (value) {
                    switch (value.id) {
                        case 1: // --- rabies
                            if (value.docs.length > 0) {
                                self.data.rabies_docs = value.docs;
                            }
                            break;
                        case 2: // --- vaccine
                            if (value.docs.length > 0) {
                                self.data.vaccine_docs = value.docs;
                            }
                            break;
                        case 3: // --- reminder
                            if (value.docs.length > 0) {
                                self.data.reminder_docs = value.docs;
                            }
                            break;
                        case 7: // --- soap
                            if (value.docs.length > 0) {
                                self.data.soap_docs = value.docs;
                            }
                            break;
                        case 8: // --- wellness
                            if (value.docs.length > 0) {
                                self.data.wellness_docs = value.docs;
                            }
                            break;
                    }
                });

                // Auto select Invoice (4) and Wellness Plan (8)
                let defaultDoc = _.filter(self.data.documentsList, doc =>
                    _.includes([4, 8], doc.id)
                );
                self.data.documents = defaultDoc;

                self.data.account_balance = get(
                    response,
                    "resource.account.balance_currency"
                );
                self.data.acct_balance = _.get(
                    response,
                    "resource.account.balance"
                );

                self.data.payment_amount = self.data.acct_balance;
                self.data.currency_symbol = this.$root.setting.currency_symbol;
                self.data.export_types =
                    _.get(response, "resource.export_options") || [];
                self.data.export_type = 0;
                self.data.patient_ids =
                    _.get(response, "resource.patient_ids") || [];

                return self.data.invoicesList;
            }
        },
        getParams() {
            let params = {};
            params.id = this.data.id;
            params.client_id = this.data.client.id;
            params.staff_id = this.data.staff_id;
            params.invoices = _.map(this.data.invoices, invoice => invoice.id);
            params.credits = _.map(this.data.credits, credit => credit.id);
            params.swipe_unique = null;
            params.status_id = this.data.status;
            params.signature_cc = this.data.signature_cc;
            params.checkout_date = this.data.checkout_date;
            params.checkout_time = this.data.checkout_date;
            params.invoice_amount = this.data.invoice_amount;
            params.credit_amount = this.data.credit_amount;
            params.payment_amount = this.data.payment_amount;
            params.record_id = _.get(this, "data.rabies.record_id");
            params.tag = this.data.rabies_tag;
            params.export_type = this.data.export_type;
            params.change = this.data.change;
            params.export_message = this.data.export_message;
            params.export_types = this.data.export_types;
            params.payment_method_id = this.data.payment_method_name
                ? this.data.payment_method_name.id
                : null;
            params.payment_method_name = this.data.payment_method_name
                ? this.data.payment_method_name.label
                : null;
            params.payment_type = this.data.payment_type;
            params.terminal = this.data.terminal;
            params.anet_transaction_id = this.data.anet_transaction_id;
            params.anet_transaction_output = this.data.anet_transaction_output;
            params.anet_transaction_name = this.data.anet_transaction_name;
            params.includeDeclined = this.data.includeDeclined;

            return params;
        },
        // -- Overwrite _selectChange helper
        _selectChange() {
            if (
                this.change_amount > 0 &&
                this.has_invoice &&
                !this.hasDeposit && // Don't have Deposits
                !this.isApplyToFund // Don't have Fund in Apply To Field
            ) {
                if (this.is_cash_selected) {
                    this.data.change = "1";
                } else {
                    this.data.change = "2";
                }
            } else {
                this.data.change = "0";
            }
        },
        updateCollected() {
            let invTotal = 0;
            if (this.data.invoices) {
                _.forEach(this.data.invoices, element => {
                    if (element.id !== 0 && element.type !== FUND) {
                        // --- skip deposit or fund if selected
                        invTotal = invTotal + element.balance * 1;
                    }
                });

                this.data.selectedInvoicesTotal = invTotal;
                this.data.outstanding_amount = invTotal;
            }

            if (this.data.credits) {
                this.data.selectedCreditsTotal = this.data.credits.reduce(
                    (total, credit) => total + credit.balance * 1,
                    0
                );
                this.data.credit_amount = this.data.selectedCreditsTotal;
            }

            let newCollected = (
                this.data.selectedInvoicesTotal - this.data.selectedCreditsTotal
            ).toFixed(2);

            this.data.payment_amount = newCollected;
            this.invTotalMinusCredits = newCollected;
        },
        async getEmailMessages(client_id) {
            this.$emit("loading");

            let invoices_param = [];
            if (this.data.invoices.length > 0) {
                _.forEach(this.data.invoices, element => {
                    invoices_param.push(element.id);
                });
            }

            let payment_name_param = _.get(
                this,
                "data.payment_type_object.name",
                ""
            );

            let params = [];
            params[0] = {
                client_id: client_id,
                recipient_id: client_id,
                recipient_type: 0,
                patient_id: this.data.patient_ids,
                invoice_id: invoices_param,
                skip_appointment: true,
                email_type: 21,
                payment_params: {
                    amount: this.data.payment_amount,
                    type: payment_name_param
                }
            };

            let data = await $.post(
                process.env.APPS +
                    "index.php/common/appointment/getEmailTemplate",
                { params }
            );

            if (data && data.response) {
                this.data.export_message =
                    data.response[0].email_template.message;
            } else {
                this._message("Error retriving email message", "danger");
            }

            this.$emit("loaded");
        }
    }
};
</script>
